<template>
  <div class="login-step login-step-otp" :class="{ 'login-step-otp-mobile': loginMethod !== 'email' }">
    <div class="step-header">
      <button class="back-btn" @click="$emit('go-back')" t_label="btn_emp2_Back_OTPStep">
        <img 
          src="https://cdn.adsalecdn.com/EMP/2-0/BuyerIdx/All_Categories.svg" 
          alt="back" 
          class="back-icon"
        />
      </button>
      <h1 class="step-title">{{ $t('lbl_emp2_EnterVerificationCode') }}</h1>
    </div>

    <div class="form-group">
      <div class="otp-info">
        <template v-if="loginMethod === 'email'">
          <div class="otp-info-line otp-info-line-email">
            <span class="otp-info-text">{{ $t('lbl_emp2_CodeSentToEmail', { emailInput: '' }).replace(': ', '').replace('\n', '') }}</span>
            <span class="otp-info-target">{{ email }}</span>
          </div>
        </template>
        <template v-else>
          <div class="otp-info-line">
            <span class="otp-info-text">{{ $t('lbl_emp2_CodeSentToMobile', { countryCode: '', mobileNumber: '' }).replace('. ', '') }}</span>
            <span class="otp-info-target">{{ countryCodePlain }} {{ mobileNumber }}</span>
          </div>
        </template>
      </div>
      <div class="otp-input-group">
        <label class="otp-label">{{ $t('lbl_emp2_PleaseEnterCodeToLogin') }}:</label>
        <div class="otp-inputs" :class="{ 'otp-error': otpError }">
          <input
            v-for="(digit, index) in otpDigits"
            :key="index"
            :value="digit"
            type="text"
            class="otp-input"
            :class="{ 'otp-input-error': otpError }"
            maxlength="1"
            @input="handleOtpInput(index, $event)"
            @keydown="handleOtpKeydown(index, $event)"
            @paste="handleOtpPaste($event)"
            @focus="$emit('clear-otp-error')"
          />
        </div>
        <!-- 错误信息 -->
        <div v-if="otpError" class="otp-error-message">
          {{ $t('lbl_emp2_InvalidVerificationCode') }}
        </div>
      </div>
      <div class="otp-help-text">
        <!-- Email OTP 提示：使用翻译里的 HTML（spam / blocked messages 加粗） -->
        <template v-if="loginMethod === 'email'">
          <p v-html="helpTextEmail"></p>
        </template>

        <!-- Mobile OTP 提示：按设计手动拆分，spam / blocked messages 加粗 -->
        <template v-else>
          <!-- 英文：严格按照设计拆成 5 段，小字 + 加粗 -->
          <p v-if="mobileHelpParts">
            <span>{{ mobileHelpParts.prefix }}</span>
            <span class="otp-help-strong">{{ mobileHelpParts.spam }}</span>
            <span>{{ mobileHelpParts.middle }}</span>
            <span class="otp-help-strong">{{ mobileHelpParts.blocked }}</span>
            <span>{{ mobileHelpParts.suffix }}</span>
          </p>

          <!-- 非英文：直接用翻译文本（包含 HTML 标签，需要使用 v-html） -->
          <p v-else v-html="helpTextMobilePlain"></p>
        </template>
      </div>
      <button
        class="btn-resend"
        :disabled="resendCountdown > 0"
        @click="$emit('resend-otp')"
        t_label="btn_emp2_ResendOTP_OTPStep"
      >
        {{ resendCountdown > 0 ? $t('lbl_emp2_ResendAfterSec', { countDown: resendCountdown }) : $t('lbl_emp2_ResendCode') }}
      </button>
    </div>

    <slot name="divider">
      <div class="divider">
        <span>{{ $t('lbl_emp2_OR') }}</span>
      </div>
    </slot>

    <slot name="alternative-login">
      <div class="alternative-login">
        <button class="btn-secondary" @click="$emit('switch-login', 'password')" t_label="btn_emp2_LoginByPassword_FromOTP">
          <div class="btn-secondary-content">
            <template v-if="loginMethod === 'email'">
              <span class="btn-secondary-text">{{ passwordButtonTextEmail.prefix }}</span>
              <span class="btn-secondary-target">{{ passwordButtonTextEmail.target }}</span>
            </template>
            <template v-else>
              <span class="btn-secondary-text">{{ passwordButtonTextMobile.prefix }}</span>
              <span class="btn-secondary-target">{{ passwordButtonTextMobile.target }}</span>
            </template>
          </div>
        </button>
      </div>
    </slot>
  </div>
</template>

<script>
export default {
  name: 'OTPStep',
  props: {
    otpDigits: {
      type: Array,
      default: () => ['', '', '', '']
    },
    otpError: {
      type: Boolean,
      default: false
    },
    loginMethod: {
      type: String,
      required: true
    },
    email: {
      type: String,
      default: ''
    },
    mobileNumber: {
      type: String,
      default: ''
    },
    countryCode: {
      type: String,
      default: '+86'
    },
    resendCountdown: {
      type: Number,
      default: 0
    }
  },
  computed: {
    countryCodePlain() {
      // i18n 文案里已包含前缀 '+'（如 "+{countryCode}"），因此这里要去掉可能的 '+'
      return String(this.countryCode || '').replace(/^\+/, '');
    },
    helpTextEmail() {
      // 确保 HTML 标签不被转义
      const text = this.$t('lbl_emp2_IfNotReceivedEmailVerification');
      // 如果被转义了，手动解码
      if (text.includes('&lt;')) {
        const div = document.createElement('div');
        div.innerHTML = text;
        return div.innerHTML;
      }
      return text;
    },
    // Mobile OTP：普通文本（不带 HTML），供非英文直接使用
    helpTextMobilePlain() {
      return this.$t('lbl_emp2_IfNotReceivedSMSVerification');
    },
    // Mobile OTP：英文时拆成 5 段，用于精确控制哪些词加粗
    mobileHelpParts() {
      const locale = (this.$i18n && this.$i18n.locale) || 'en';
      if (locale !== 'en') {
        return null;
      }
      // 使用翻译文本并拆分
      const fullText = this.helpTextMobilePlain;
      // 按照设计稿拆分：查找 "spam" 和 "blocked messages"
      const spamIndex = fullText.toLowerCase().indexOf('spam');
      const blockedIndex = fullText.toLowerCase().indexOf('blocked messages');
      
      if (spamIndex !== -1 && blockedIndex !== -1) {
        return {
          prefix: fullText.substring(0, spamIndex),
          spam: 'spam',
          middle: fullText.substring(spamIndex + 4, blockedIndex),
          blocked: 'blocked messages',
          suffix: fullText.substring(blockedIndex + 16)
        };
      }
      // 如果无法拆分，返回 null 使用普通文本
      return null;
    },
    // 底部按钮文本拆分 - Email
    passwordButtonTextEmail() {
      const fullText = this.$t('lbl_emp2_LoginByPasswordEmail', { emailInput: this.email });
      const locale = (this.$i18n && this.$i18n.locale) || 'en';
      
      if (locale === 'en') {
        // 英文: "Log in {email} by password" -> 拆分为 "Enter password to log in " 和 "{email}"
        const match = fullText.match(/^Log in (.+?) by password$/);
        if (match) {
          return {
            prefix: 'Enter password to log in ',
            target: match[1]
          };
        }
      } else {
        // 中文和其他语言：尝试拆分
        // 繁体: "使用密碼登錄 {email}"
        // 简体: "使用密码登录 {email}"
        const zhMatch = fullText.match(/^(使用[密碼密码]登錄?)\s*(.+)$/);
        if (zhMatch) {
          return {
            prefix: zhMatch[1] + ' ',
            target: zhMatch[2]
          };
        }
      }
      
      // 如果无法拆分，返回原文本作为prefix
      return {
        prefix: fullText,
        target: ''
      };
    },
    // 底部按钮文本拆分 - Mobile
    passwordButtonTextMobile() {
      const fullText = this.$t('lbl_emp2_LoginByPasswordMobile', { 
        countryCode: this.countryCodePlain, 
        mobileNumber: this.mobileNumber 
      });
      const locale = (this.$i18n && this.$i18n.locale) || 'en';
      
      if (locale === 'en') {
        // 英文: "Log in +{countryCode} {mobileNumber} by Password" -> 拆分为 "Enter password to log in " 和 "+{countryCode} {mobileNumber}"
        const match = fullText.match(/^Log in (.+?) by Password$/);
        if (match) {
          return {
            prefix: 'Enter password to log in ',
            target: match[1]
          };
        }
      } else {
        // 中文和其他语言：尝试拆分
        // 繁体: "使用密碼登錄 +{countryCode} {mobileNumber}"
        // 简体: "使用密码登录 +{countryCode} {mobileNumber}"
        const zhMatch = fullText.match(/^(使用[密碼密码]登錄?)\s*(.+)$/);
        if (zhMatch) {
          return {
            prefix: zhMatch[1] + ' ',
            target: zhMatch[2]
          };
        }
      }
      
      // 如果无法拆分，返回原文本作为prefix
      return {
        prefix: fullText,
        target: ''
      };
    }
  },
  methods: {
    handleOtpInput(index, event) {
      const value = event.target.value.replace(/[^0-9]/g, '').replace(/\s/g, '');
      const newDigits = [...this.otpDigits];
      newDigits[index] = value;
      this.$emit('update:otpDigits', newDigits);
      
      // 自动聚焦到下一个输入框
      if (value && index < this.otpDigits.length - 1) {
        const nextInput = event.target.parentElement.children[index + 1];
        if (nextInput) {
          nextInput.focus();
        }
      }
    },
    handleOtpKeydown(index, event) {
      // 处理退格键
      if (event.key === 'Backspace' && !this.otpDigits[index] && index > 0) {
        const prevInput = event.target.parentElement.children[index - 1];
        if (prevInput) {
          prevInput.focus();
        }
      }
    },
    handleOtpPaste(event) {
      event.preventDefault();
      const pastedData = event.clipboardData.getData('text').replace(/[^0-9]/g, '').replace(/\s/g, '').slice(0, this.otpDigits.length);
      const newDigits = [...this.otpDigits];
      for (let i = 0; i < pastedData.length && i < newDigits.length; i++) {
        newDigits[i] = pastedData[i];
      }
      this.$emit('update:otpDigits', newDigits);
      
      // 聚焦到最后一个填充的输入框
      const lastFilledIndex = Math.min(pastedData.length - 1, this.otpDigits.length - 1);
      const nextInput = event.target.parentElement.children[lastFilledIndex + 1];
      if (nextInput) {
        nextInput.focus();
      } else {
        event.target.blur();
      }
    }
  }
};
</script>

<style scoped>
.login-step-otp {
  gap: 24px !important;
}

.step-header {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
}

.back-btn {
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  flex-shrink: 0;
}

.back-btn .back-icon {
  width: 20px;
  height: 20px;
  transform: scaleX(-1);
}

.back-btn:hover {
  background: rgba(0, 0, 0, 0.05);
  border-radius: 50%;
}

.step-title {
  color: rgba(0, 0, 0, 0.80);
  font-size: 28px;
  font-family: 'Inter', sans-serif;
  font-weight: 700;
  line-height: 36.40px;
  margin: 0;
  word-wrap: break-word;
  flex: 1;
}

/* Mobile OTP 标题样式：24px 以统一移动端字号 */
.login-step-otp-mobile .step-title {
  font-size: 24px;
  line-height: 31.20px;
}

.form-group {
  align-self: stretch;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  display: flex;
  gap: 16px !important;
  width: 100%;
}

.otp-info {
  align-self: stretch;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 4px !important;
  display: flex;
  width: 100%;
}

.otp-info-line {
  align-self: stretch;
  justify-content: flex-start;
  align-items: center;
  gap: 8px !important;
  display: inline-flex;
  flex-wrap: wrap;
  align-content: center;
  width: 100%;
}

/* Email验证码：邮箱地址换行显示 */
.otp-info-line-email {
  flex-direction: column;
  align-items: flex-start;
}

.otp-info-text {
  color: rgba(0, 0, 0, 0.80);
  font-size: 14px;
  font-family: 'Inter', sans-serif;
  font-weight: 400;
  line-height: 18.20px;
  word-wrap: break-word;
}

.otp-info-target {
  color: rgba(0, 0, 0, 0.80);
  font-size: 14px;
  font-family: 'Inter', sans-serif;
  font-weight: 700;
  line-height: 18.20px;
  word-wrap: break-word;
}

.otp-input-group {
  align-self: stretch;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 8px !important;
  display: flex;
  width: 100%;
}

.otp-label {
  color: rgba(0, 0, 0, 0.80);
  font-size: 12px !important;
  font-family: 'Inter', sans-serif;
  font-weight: 400;
  line-height: 15.60px;
  word-wrap: break-word;
}

.otp-inputs {
  justify-content: flex-start;
  align-items: flex-start;
  gap: 4px;
  display: inline-flex;
}

.otp-inputs.otp-error {
  border-color: #CD1515;
}

.otp-input {
  width: 32px;
  height: auto;
  min-height: 32px;
  padding-left: 12px;
  padding-right: 12px;
  padding-top: 0;
  padding-bottom: 0;
  background: white;
  border-radius: 4px;
  outline: 1px rgba(0, 0, 0, 0.23) solid;
  outline-offset: -1px;
  border: none;
  text-align: center;
  font-size: 24px;
  font-family: 'Inter', sans-serif;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.80);
  transition: border-color 0.2s, background 0.2s;
  box-sizing: border-box;
}

.otp-input:focus {
  outline: 1px rgba(0, 0, 0, 0.23) solid;
  outline-offset: -1px;
  background: white;
}

.otp-input.otp-input-error {
  border-color: #CD1515 !important;
}

.otp-error-message {
  padding-left: 14px;
  padding-right: 14px;
  color: #D32F2F;
  font-size: 12px;
  font-family: 'Inter', sans-serif;
  font-weight: 400;
  letter-spacing: 0.40px;
  word-wrap: break-word;
}

.otp-help-text {
  align-self: stretch;
  word-wrap: break-word;
  width: 100%;
  margin: 0;
  padding: 0;
}

/* 小字说明统一 12px / 400 / 15.6px，灰色 60% - 适用于email和mobile验证码 */
.otp-help-text p,
.otp-help-text span {
  margin: 0;
  color: rgba(0, 0, 0, 0.60);
  font-size: 12px !important;
  letter-spacing: normal !important;
  font-weight: 400;
  line-height: 15.60px;
  word-wrap: break-word;
}

.otp-help-text strong,
.otp-help-text .otp-help-strong {
  color: rgba(0, 0, 0, 0.60);
  font-size: 12px !important;
  font-family: 'Inter', sans-serif;
  font-weight: 700;
  line-height: 15.60px;
  word-wrap: break-word;
}

.btn-resend {
  width: 100%;
  height: 42px;
  padding: 0 24px;
  background: #5D49C4; /* 原型启用态背景 */
  color: white;
  border: none;
  border-radius: 999px;
  font-size: 14px;
  font-family: 'Inter', sans-serif;
  font-weight: 700;
  line-height: 21px;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  transition: all 0.2s;
  margin: 0;
  text-align: center;
  box-sizing: border-box;
}

.btn-resend:hover:not(:disabled) {
  background: #4a3ba5;
}

.btn-resend:disabled {
  background: rgba(0, 18, 26, 0.38); /* 原型倒计时禁用态背景 */
  color: white; /* 原型文字颜色：白色 */
  cursor: not-allowed;
  opacity: 1;
}

.divider {
  display: flex;
  align-items: center;
  gap: 24px;
  margin: 0;
}

.divider::before,
.divider::after {
  content: '';
  flex: 1;
  height: 1px;
  background: rgba(0, 0, 0, 0.10);
}

.divider span {
  color: rgba(0, 0, 0, 0.50);
  font-size: 14px;
  font-family: 'Inter', sans-serif;
  font-weight: 600;
  line-height: 14px;
}

.alternative-login {
  width: 100%;
  height: 100%;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 16px;
  display: inline-flex;
  margin: 0;
  padding: 0;
}

.btn-secondary {
  align-self: stretch;
  height: 42px;
  padding: 0 24px;
  border-radius: 999px;
  outline: 1px rgba(0, 0, 0, 0.60) solid;
  outline-offset: -1px;
  justify-content: center;
  align-items: center;
  gap: 8px;
  display: inline-flex;
  background: white !important;
  background-color: white !important;
  border: none !important;
  cursor: pointer;
  transition: all 0.2s;
  width: 100%;
  box-sizing: border-box;
}

.btn-secondary:hover {
  background: rgba(0, 0, 0, 0.03) !important;
  background-color: rgba(0, 0, 0, 0.03) !important;
  outline-color: rgba(0, 0, 0, 0.60);
}

.btn-secondary-content {
  flex: 1 1 0;
  text-box-trim: trim-both;
  text-box-edge: cap alphabetic;
  text-align: center;
  justify-content: center;
  align-items: center;
  display: flex;
  flex-direction: column;
  width: 100%;
  line-height: 14px;
}

.btn-secondary-text {
  color: rgba(0, 0, 0, 0.80);
  font-size: 14px;
  font-family: 'Inter', sans-serif;
  font-weight: 600;
  line-height: 14px;
  word-wrap: break-word;
  display: inline;
}

.btn-secondary-target {
  color: rgba(0, 0, 0, 0.80);
  font-size: 14px;
  font-family: 'Inter', sans-serif;
  font-weight: 700;
  line-height: 14px;
  word-wrap: break-word;
  display: inline;
}
</style>

<!-- 全局样式覆盖 -->
<style>
/* 引入共享样式 */
@import '../Common/new-login-shared.css';

.login-step-otp .alternative-login {
  margin: 0;
  gap: 0;
}

.alternative-login .btn-secondary {
  background: white !important;
  background-color: white !important;
  border: none !important;
  color: rgba(0, 0, 0, 0.80) !important;
}

.alternative-login .btn-secondary:hover {
  background: rgba(0, 0, 0, 0.03) !important;
  background-color: rgba(0, 0, 0, 0.03) !important;
  color: rgba(0, 0, 0, 0.80) !important;
}
</style>

